/**
 * Bennji Lists — recently-viewed + wishlist, localStorage-backed, no app required.
 * Product cards are fetched from Horizon's own section rendering endpoint
 * (?section_id=section-rendering-product-card), so styling always matches the theme.
 */
(function () {
  'use strict';

  const RV_KEY = 'bennji:recently-viewed';
  const WL_KEY = 'bennji:wishlist';
  const RV_MAX = 12;
  const CHANGE_EVENT = 'bennji:wishlist:change';

  const read = (key) => {
    try { return JSON.parse(localStorage.getItem(key)) || []; } catch (e) { return []; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  };

  const wishlist = {
    all: () => read(WL_KEY),
    has: (handle) => read(WL_KEY).includes(handle),
    toggle(handle) {
      let list = read(WL_KEY);
      if (list.includes(handle)) list = list.filter((h) => h !== handle);
      else list = [handle, ...list];
      write(WL_KEY, list);
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
      return list.includes(handle);
    },
  };

  /* Full product cards are harvested from the all-products collection page —
     one cached fetch serves every shelf and the wishlist dialog, and the
     markup is guaranteed to match the theme's real cards. */
  let catalogPromise = null;
  function getCardCatalog() {
    if (!catalogPromise) {
      catalogPromise = fetch('/collections/all-products')
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const map = new Map();
          doc.querySelectorAll('.product-card').forEach((card) => {
            const link = card.querySelector('.product-card__link');
            const match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
            if (match && !map.has(match[1])) {
              map.set(match[1], card.closest('product-card') || card);
            }
          });
          return map;
        })
        .catch(() => new Map());
    }
    return catalogPromise;
  }

  async function renderCards(handles, listEl) {
    const catalog = await getCardCatalog();
    listEl.innerHTML = '';
    let count = 0;
    for (const handle of handles) {
      const card = catalog.get(handle);
      if (!card) continue;
      const item = document.createElement('div');
      item.className = 'resource-list__item';
      item.appendChild(card.cloneNode(true));
      // Dynamically inserted lazy images never meet the initial fade observer —
      // mark them loaded so the opacity fade can't hide them.
      item.querySelectorAll('img[loading="lazy"]').forEach((img) => img.classList.add('loaded'));
      listEl.appendChild(item);
      count++;
    }
    return count;
  }

  /* Records the current product on PDPs. */
  customElements.define('recently-viewed-tracker', class extends HTMLElement {
    connectedCallback() {
      const handle = this.dataset.handle;
      if (!handle) return;
      const list = [handle, ...read(RV_KEY).filter((h) => h !== handle)].slice(0, RV_MAX);
      write(RV_KEY, list);
    }
  });

  /* Renders the shelf; removes its section when there is nothing to show. */
  customElements.define('recently-viewed-shelf', class extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.exclude || '';
      const limit = parseInt(this.dataset.limit || '4', 10);
      const handles = read(RV_KEY).filter((h) => h !== exclude).slice(0, limit);
      const section = this.closest('.shopify-section') || this;
      if (!handles.length) { section.remove(); return; }
      const shown = await renderCards(handles, this.querySelector('[data-shelf-list]'));
      if (!shown) section.remove();
      else this.removeAttribute('hidden');
    }
  });

  /* Heart toggle (cards + PDP). */
  customElements.define('wishlist-toggle', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.sync();
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        wishlist.toggle(this.dataset.handle);
      });
      document.addEventListener(CHANGE_EVENT, () => this.sync());
    }
    sync() {
      const saved = wishlist.has(this.dataset.handle);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.button.setAttribute('aria-label', saved ? 'Remove from saved items' : 'Save for later');
      this.classList.toggle('is-saved', saved);
    }
  });

  /* Header icon + dialog. */
  customElements.define('wishlist-drawer', class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-wishlist-open]');
      this.dialog = this.querySelector('dialog');
      this.count = this.querySelector('[data-wishlist-count]');
      this.list = this.querySelector('[data-wishlist-list]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.trigger.addEventListener('click', () => this.open());
      this.querySelector('[data-wishlist-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
      document.addEventListener(CHANGE_EVENT, () => { this.syncCount(); if (this.dialog.open) this.render(); });
      this.syncCount();
    }
    syncCount() {
      const n = wishlist.all().length;
      this.count.textContent = n;
      this.count.hidden = n === 0;
    }
    async open() {
      this.dialog.showModal();
      await this.render();
    }
    async render() {
      const handles = wishlist.all();
      this.empty.hidden = handles.length > 0;
      this.list.hidden = handles.length === 0;
      if (!handles.length) { this.list.innerHTML = ''; return; }
      this.list.setAttribute('aria-busy', 'true');
      await renderCards(handles, this.list);
      this.list.removeAttribute('aria-busy');
    }
  });
})();
